import { CatalogueRollout } from "routes/createContent/api/catalogueDeployments";
import { CatalogueDeploymentRow } from "routes/createContent/components/CatalogueDeploymentRow";
import { EditorToolbar } from "routes/createContent/components/EditorToolbar";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, FormControl, IconButton, InputLabel, MenuItem, Select, Tooltip } from "@material-ui/core";
import { ArrowBack } from "@material-ui/icons";
import { css } from "@emotion/css";
import { Breakpoint } from "theme";
import { isContentOnly } from "utils/appMode";
import { FormProvider, useForm } from "react-hook-form";
import { Link, useHistory } from "react-router-dom";
import { CataloguePath } from "routes/createContent/paths";
import { ConfirmDeleteDialog } from "routes/createContent/components/CatalogueList";
import LessonMetadataSection from "../components/LessonMetadataSection";
import { LessonSaveButton, LessonValidationIssues } from "../components/LessonSaveControls";
import PartsSection from "../components/PartsSection";
import PreviewColumn from "../components/preview/PreviewColumn";
import { useSaveLesson } from "routes/createContent/api/useSaveLesson";
import { useDeleteLesson } from "routes/createContent/api/useDeleteLesson";
import { LessonFormData } from "../types";
import { LOCALES } from "../constants";
import { getErrorMessage } from "../utils";
import { useActiveLocale } from "../ActiveLocaleContext";

type Props = {
  initialValues: LessonFormData;
  rollout?: CatalogueRollout;
  /** Called after a successful save of an existing lesson so the parent can
   *  remount the editor against the freshly-refetched server state. */
  onSaved?: () => void;
};

const LessonEditor: React.VFC<Props> = ({ initialValues, rollout, onSaved }) => {
  const { t } = useTranslation();
  const history = useHistory();
  // shouldUnregister: false is REQUIRED — in RHF v6 the default is `true`, which
  // causes getValues() to return only the output of registered Controllers,
  // discarding anything carried in shallow field state. We deliberately keep
  // part.id and translationIds out of Controllers (they're metadata, not
  // inputs), so without shallow merging they'd be stripped from every save
  // payload and the backend would treat every save as a create.
  const form = useForm<LessonFormData>({ defaultValues: initialValues, shouldUnregister: false });
  const save = useSaveLesson();
  const del = useDeleteLesson();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isEditing = initialValues.id != null;
  const { activeLocale, setActiveLocale } = useActiveLocale();
  const { register } = form;

  React.useEffect(() => {
    register("id");
  }, [register]);

  const onSubmit = form.handleSubmit(async () => {
    const data = form.getValues();
    if (initialValues.creator_id != null) {
      if (!window.confirm(t("creator.confirm_lesson"))) return;
    }
    try {
      const res = await save.mutateAsync(data);
      if (!isEditing) {
        // LessonEditorPage will refetch and remount the editor with the fresh
        // saved data once the URL changes.
        history.replace(CataloguePath.Lessons_Edit.replace(":id", String(res.id)));
        return;
      }
      // Ask the parent to remount with the refetched server state. This is the
      // only reliable way to sync all of RHF's nested useFieldArray instances
      // (parts → content blocks → questions → answers) with the post-save
      // values: setValue/reset on the top-level `parts` path doesn't propagate
      // to the nested field arrays in RHF v6, so newly-added blocks would
      // otherwise disappear until a manual page reload.
      onSaved?.();
    } catch {
      // On partial failure, keep any ids useSaveLesson already assigned so a
      // retry doesn't re-POST the same parts/translations.
      form.reset(data);
    }
  });

  const onDelete = () => {
    if (!initialValues.id) return;
    del.mutate(initialValues.id, {
      onSuccess: () => {
        setConfirmDelete(false);
        history.push(CataloguePath.Lessons);
      },
    });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={onSubmit}>
        <EditorToolbar
          actions={
            <>
              {isEditing && (
                <Button
                  size="small"
                  style={{ color: "#f44336" }}
                  onClick={() => {
                    del.reset();
                    setConfirmDelete(true);
                  }}
                  disabled={del.isLoading}
                >
                  Delete lesson
                </Button>
              )}
              <LessonSaveButton isEditing={isEditing} saving={save.isLoading} />
            </>
          }
        >
          <Tooltip title="Back to lessons">
            <IconButton size="small" component={Link} to={CataloguePath.Lessons}>
              <ArrowBack />
            </IconButton>
          </Tooltip>
          <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
            <InputLabel>Editing locale</InputLabel>
            <Select
              label="Editing locale"
              value={activeLocale}
              onChange={e => setActiveLocale(e.target.value as string)}
            >
              {LOCALES.map(l => (
                <MenuItem key={l.code} value={l.code}>
                  {l.flag} {l.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isContentOnly && initialValues.name && (
            <CatalogueDeploymentRow kind="lesson" name={initialValues.name} rollout={rollout} />
          )}
        </EditorToolbar>

        <Box className={isContentOnly ? `${styles.layout} ${styles.stacking}` : styles.layout}>
          <Box className={styles.editorColumn}>
            <LessonMetadataSection isEditing={isEditing} />
            <PartsSection />
            <LessonValidationIssues />
            {save.isError && (
              <Box mt={1} color="error.main">
                {save.error ? getErrorMessage(save.error) : "Failed to save"}
              </Box>
            )}
            {del.isError && (
              <Box mt={1} color="error.main">
                {getErrorMessage(del.error)}
              </Box>
            )}
          </Box>
          <Box className={styles.previewColumn}>
            <PreviewColumn />
          </Box>
        </Box>
      </form>
      <ConfirmDeleteDialog
        noun="lesson"
        open={confirmDelete}
        loading={del.isLoading}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={onDelete}
      />
    </FormProvider>
  );
};

export default LessonEditor;

const styles = {
  layout: css`
    display: grid;
    grid-template-columns: 1fr 360px;
    gap: 24px;
    padding: 16px;
    align-items: start;
  `,
  stacking: css`
    grid-template-columns: minmax(0, 1fr) 360px;

    @media screen and (max-width: ${Breakpoint.md}px) {
      grid-template-columns: minmax(0, 1fr);

      & > * {
        position: static;
      }
    }
  `,
  editorColumn: css`
    min-width: 0;
  `,
  previewColumn: css`
    position: sticky;
    top: 16px;
  `,
};
